import { baseApi } from './baseApi';
import type { ApiResponse, ProviderConfig, ProviderBalance } from '../../types/api';

interface CreateProviderConfigRequest {
  providerId: string;
  apiKeys?: {
    apiKey?: string;
  };
  endpoints?: {
    baseUrl?: string;
  };
  margins?: Record<string, number>;
  maintenance?: Record<string, unknown>;
}

interface UpdateProviderConfigRequest {
  providerId?: string;
  apiKeys?: {
    apiKey?: string;
  };
  endpoints?: {
    baseUrl?: string;
  };
  margins?: Record<string, number>;
  maintenance?: Record<string, unknown>;
}

export const providerConfigsApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getProviderConfigs: builder.query<ApiResponse<{ configs: ProviderConfig[] }>, void>({
      query: () => ({
        url: '/provider-configs',
        method: 'GET',
      }),
      providesTags: ['ProviderConfig'],
    }),
    getProviderConfigById: builder.query<ApiResponse<{ config: ProviderConfig }>, string>({
      query: (providerId) => ({
        url: `/provider-configs/${providerId}`,
        method: 'GET',
      }),
      providesTags: (_result, _error, providerId) => [{ type: 'ProviderConfig', id: providerId }],
    }),
    createProviderConfig: builder.mutation<
      ApiResponse<{ config: ProviderConfig; message: string }>,
      CreateProviderConfigRequest
    >({
      query: (data) => ({
        url: '/provider-configs',
        method: 'POST',
        body: data,
      }),
      invalidatesTags: ['ProviderConfig'],
    }),
    updateProviderConfig: builder.mutation<
      ApiResponse<{ config: ProviderConfig; message: string }>,
      { providerId: string; data: UpdateProviderConfigRequest }
    >({
      query: ({ providerId, data }) => ({
        url: `/provider-configs/${providerId}`,
        method: 'PUT',
        body: data,
      }),
      invalidatesTags: (_result, _error, { providerId }) => [
        { type: 'ProviderConfig', id: providerId },
        'ProviderConfig',
      ],
    }),
    patchProviderConfig: builder.mutation<
      ApiResponse<{ config: ProviderConfig; message: string }>,
      { providerId: string; data: Partial<UpdateProviderConfigRequest> }
    >({
      query: ({ providerId, data }) => ({
        url: `/provider-configs/${providerId}`,
        method: 'PATCH',
        body: data,
      }),
      invalidatesTags: (_result, _error, { providerId }) => [
        { type: 'ProviderConfig', id: providerId },
        'ProviderConfig',
      ],
    }),
    deleteProviderConfig: builder.mutation<ApiResponse<{ message: string }>, string>({
      query: (providerId) => ({
        url: `/provider-configs/${providerId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['ProviderConfig'],
    }),
    getProviderBalance: builder.query<ApiResponse<ProviderBalance>, string>({
      query: (providerId) => ({
        url: `/provider-configs/${providerId}/balance`,
        method: 'GET',
      }),
      providesTags: (_result, _error, providerId) => [{ type: 'ProviderConfig', id: `${providerId}-balance` }],
    }),
  }),
});

export const {
  useGetProviderConfigsQuery,
  useGetProviderConfigByIdQuery,
  useCreateProviderConfigMutation,
  useUpdateProviderConfigMutation,
  usePatchProviderConfigMutation,
  useDeleteProviderConfigMutation,
  useGetProviderBalanceQuery,
} = providerConfigsApi;

